/**
 * RitualCard Component
 * Displays a ritual with full-bleed cover image, user avatar, and metadata
 * Card with bottom gradient overlay for text readability
 */

import { Bookmark } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { TagChip } from "@/components/shared/TagChip"
import { Avatar } from "@/components/ui/avatar"
import { Card } from "@/components/ui/card"

export interface RitualCardProps {
  id: string
  image?: string
  userAvatar?: string
  username: string
  userHandle: string
  title: string
  tags: string[]
  duration: string
  contextualTag?: string
  isBookmarked?: boolean
  onBookmarkClick?: () => void
  userId?: string
  price?: number
  participationFee?: "Free" | "Paid" | "Donation-based"
  organizersCount?: number
}

export function RitualCard({
  id,
  image,
  userAvatar,
  username,
  title,
  tags,
  isBookmarked = false,
  onBookmarkClick,
  userId,
  price,
  participationFee,
  organizersCount,
}: RitualCardProps) {
  const navigate = useNavigate()
  const avatarFallback = username?.[0] || "?"

  // Determine price badge content and styling
  const priceBadge =
    participationFee === "Free"
      ? { content: "Free", bgClass: "bg-brand-olive" }
      : participationFee === "Donation-based"
        ? { content: "Donation-based", bgClass: "bg-brand-terracotta" }
        : participationFee === "Paid" && price !== undefined
          ? { content: `$${price}`, bgClass: "bg-brand-terracotta" }
          : null

  return (
    <Card
      className="overflow-hidden h-full w-full aspect-16/11 border-0 bg-transparent p-0 cursor-pointer rounded-none"
      onClick={() => navigate(`/rituals/${id}`)}
    >
      <div className="relative h-full w-full overflow-hidden rounded-tl-[20px] rounded-br-[20px] md:rounded-tl-[40px] md:rounded-br-[40px]">
        {/* Cover Image */}
        {image ? (
          <img src={image} alt={title} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-card" />
        )}

        {/* Top Section - User Info and Bookmark (positioned absolutely) */}
        <div className="absolute top-5 left-5 right-5 z-10 flex items-start justify-between">
          {/* User Profile */}
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                if (userId) navigate(`/profile/${userId}`)
              }}
              className="cursor-pointer hover:scale-105 transition-transform"
              aria-label={`View ${username}'s profile`}
            >
              <Avatar
                src={userAvatar}
                fallback={avatarFallback}
                size="lg"
                className="w-[30px] h-[30px]"
              />
            </button>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-primary opacity-90 leading-normal">
                {username}
              </span>
              {organizersCount !== undefined && organizersCount > 1 && (
                <span className="text-xs text-primary/70">+ {organizersCount}</span>
              )}
            </div>
          </div>

          {/* Bookmark Button */}
          {onBookmarkClick && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onBookmarkClick()
              }}
              className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
              aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              <Bookmark
                className={`w-[22px] h-[22px] ${isBookmarked ? "fill-white text-white" : "text-primary"}`}
              />
            </button>
          )}
        </div>

        {/* Bottom Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-[84px] sm:h-29 z-10">
          {/* Blur layer with gradient mask */}
          <div
            className="absolute inset-0 backdrop-blur-[1px] bg-[rgba(0,0,0,0.9)]"
            style={{
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.15) 25%, rgba(0, 0, 0, 0.43) 55%, rgb(0,0,0) 100%)",
              maskImage:
                "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.2) 25%, rgba(0,0,0,0.6) 55%, rgb(0,0,0) 100%)",
            }}
          />

          {/* Content */}
          <div className="relative z-20 p-4 md:px-[30px] md:py-[20px] h-full">
            {/* Title */}
            <div className="absolute left-4 md:left-5 top-4 md:top-[30px] w-full">
              <h3 className="truncate text-[14px] md:text-[18px] font-semibold leading-[22px] text-primary opacity-90 max-w-[calc(100%-40px)]">
                {title}
              </h3>
            </div>

            {/* Tags and Price */}
            <div className="absolute left-4 md:left-5 top-[48px] md:top-[62px] flex items-center flex-wrap gap-1.5 md:gap-2.5">
              {priceBadge && (
                <span
                  className={`${priceBadge.bgClass} text-primary text-[13px] rounded-[5px] px-1.5 py-0.5 leading-5 opacity-90`}
                >
                  {priceBadge.content}
                </span>
              )}

              {tags.map((tag, index) => (
                <TagChip
                  key={index}
                  tag={tag}
                  tab="rituals"
                  className="px-1.5 py-0.5 rounded-[5px] leading-5 text-xs md:text-[13px] border-primary"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
